// Состояние приложения (zustand). Документ живёт в редакторе TipTap (editor.getJSON()), здесь - всё остальное:
// открытый проект, метаданные, источники, состояние сохранения и проверки, панели, масштаб.
import { createStore, useStore } from 'zustand';
import type { Editor, JSONContent } from '@tiptap/core';
import type { BuildResult, Meta, Source } from './types';
import type { LintIssue } from './registry';

export type SaveState = 'saved' | 'dirty' | 'saving' | 'error' | 'conflict';

export interface AppState {
  view: 'start' | 'editor';
  projectId: string | null;
  revision: number;
  meta: Meta | null;
  sources: Source[];
  /** документ, с которым создаётся редактор при открытии проекта */
  initialDoc: JSONContent | null;
  editor: Editor | null;
  /** code - код ошибки сервера ('conflict', 'project_deleted'…), info - её подробности (savedBy, byAdmin) */
  save: { state: SaveState; at?: string; error?: string; code?: string; info?: any };
  check: { running: boolean; progress?: string; result?: BuildResult | null; at?: number; error?: string | null };
  lint: LintIssue[];
  panels: { left: string | null; right: string | null };
  zoom: number;
  /** данные по умолчанию сервера (/api/defaults): студент, виды работ, структурные элементы */
  defaults: any;
  /** выделенный узел (id) - для подсветки находок и панели «Свойства» */
  focusNode: string | null;
  /** только просмотр: нет действующей подписки (features/auth/billing.tsx); изменения не сохраняются */
  readOnly: boolean;

  setMeta: (fn: (m: Meta) => Meta) => void;
  setSources: (fn: (s: Source[]) => Source[]) => void;
  markDirty: () => void;
  set: (patch: Partial<AppState>) => void;
}

export const appStore = createStore<AppState>()((set, get) => ({
  view: 'start',
  projectId: null,
  revision: 0,
  meta: null,
  sources: [],
  initialDoc: null,
  editor: null,
  save: { state: 'saved' },
  check: { running: false, result: null },
  lint: [],
  panels: { left: readPref('panel.left', 'structure'), right: readPref('panel.right', null) },
  zoom: Number(readPref('zoom', '1')) || 1,
  defaults: null,
  focusNode: null,
  readOnly: false,

  setMeta: (fn) => { const m = get().meta; if (m) { set({ meta: fn(m) }); get().markDirty(); } },
  setSources: (fn) => { set({ sources: fn(get().sources) }); get().markDirty(); },
  // конфликт и удалённый отчёт ждут решения пользователя (плашка): новые правки не запускают сохранение
  markDirty: () => { const s = get().save; if (!get().readOnly && s.state !== 'conflict' && s.code !== 'project_deleted') set({ save: { ...s, state: 'dirty' } }); },
  set: (patch) => set(patch),
}));

export type AppStore = typeof appStore;

/** React-хук: const meta = useApp((s) => s.meta) */
export function useApp<T>(selector: (s: AppState) => T): T {
  return useStore(appStore, selector);
}

// --- настройки конкретного пользователя браузера (панели, масштаб) - localStorage, всегда в try/catch
export function readPref(key: string, def: string | null): any {
  try { const v = localStorage.getItem('sto.' + key); return v === null ? def : (v === 'null' ? null : v); } catch { return def; }
}
export function writePref(key: string, val: unknown) {
  try { localStorage.setItem('sto.' + key, String(val)); } catch { /* недоступно - не важно */ }
}
appStore.subscribe((s, prev) => {
  if (s.panels !== prev.panels) { writePref('panel.left', s.panels.left); writePref('panel.right', s.panels.right); }
  if (s.zoom !== prev.zoom) writePref('zoom', s.zoom);
});
